import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Wallet, Loader2, Banknote, CreditCard, X, Lock, CheckCircle2, Ban, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const CARD_STYLE = { style: { base: { fontSize: "16px", color: "#1A242B", fontFamily: "inherit", "::placeholder": { color: "#8FA89B" } }, invalid: { color: "#dc2626" } } };

export default function POSBalances() {
  const qc = useQueryClient();
  const [cardRow, setCardRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["pos-balances-today"],
    queryFn: () => api.get("/pos/balances-today").then((r) => r.data),
  });
  const stripeCfg = useQuery({ queryKey: ["pos-stripe-config"], queryFn: () => api.get("/pos/stripe/config").then((r) => r.data) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["pos-balances-today"] });
    qc.invalidateQueries({ queryKey: ["pos-balances-count"] });
  };

  const takeCash = async (row) => {
    try {
      await api.post(`/pos/balances/${row.kind}/${row.id}/resolve`, { method: "cash" });
      toast.success(`Balance settled in cash — ${money(row.balance_due)}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="pos-balances-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><Wallet className="text-[#144F75]" /> Balances due today</h2>
      <p className="text-sm text-[#4A5D6B] mb-4">Members who paid a deposit online. Collect the remaining balance by cash or card — or cancel and refund/forfeit the deposit.</p>

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : rows.length === 0 ? (
        <p className="text-sm text-[#8FA89B] eyw-card p-6 text-center">No outstanding balances for today.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={`${row.kind}-${row.id}`} data-testid={`balance-${row.id}`} className="eyw-card p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#1A242B]">{row.customer_name} <span className="text-[#8FA89B] font-normal">· {row.title}</span></p>
                <p className="text-xs text-[#8FA89B]">{row.time || ""} · paid {money(row.amount_paid)} of {money(row.total_price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-serif text-lg text-[#B5794E]">{money(row.balance_due)} due</span>
                <button onClick={() => takeCash(row)} data-testid={`balance-cash-${row.id}`} className="eyw-btn-ghost !py-1.5 text-xs flex items-center gap-1"><Banknote size={14} /> Cash</button>
                <button onClick={() => setCardRow(row)} data-testid={`balance-card-${row.id}`} className="eyw-btn !py-1.5 text-xs flex items-center gap-1"><CreditCard size={14} /> Card</button>
                <button onClick={() => setCancelRow(row)} data-testid={`balance-cancel-${row.id}`} className="!py-1.5 px-3 text-xs rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1"><Ban size={14} /> Cancel</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cardRow && (
        <BalanceCardModal row={cardRow} publishableKey={stripeCfg.data?.publishable_key}
          onClose={() => setCardRow(null)}
          onPaid={() => { toast.success("Balance charged."); setCardRow(null); refresh(); }} />
      )}

      {cancelRow && (
        <CancelDepositModal row={cancelRow} onClose={() => setCancelRow(null)}
          onDone={(action, amt) => {
            toast.success(action === "refund" ? `Appointment cancelled — deposit of ${money(amt)} refunded.` : `Appointment cancelled — deposit of ${money(amt)} forfeited.`);
            setCancelRow(null); refresh();
          }} />
      )}
    </div>
  );
}

function CancelDepositModal({ row, onClose, onDone }) {
  const [loading, setLoading] = useState("");
  const deposit = Number(row.amount_paid || 0);

  const run = async (action) => {
    setLoading(action);
    try {
      const { data } = await api.post(`/pos/balances/${row.kind}/${row.id}/cancel`, { action });
      onDone(action, Number(data.deposit || deposit));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel.");
    } finally { setLoading(""); }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7" data-testid="cancel-deposit-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Ban size={22} className="text-red-500" /> Cancel appointment</h3>
          <button onClick={onClose} data-testid="cancel-deposit-close" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#4A5D6B] mb-1">{row.customer_name} · {row.title}</p>
        <p className="text-sm text-[#4A5D6B] mb-5">Deposit paid: <span className="font-semibold text-[#144F75]">{money(deposit)}</span>. How should the deposit be handled?</p>

        <button onClick={() => run("refund")} disabled={!!loading || deposit <= 0} data-testid="cancel-refund-btn" className="eyw-btn w-full justify-center mb-3 disabled:opacity-60 flex items-center gap-2">
          {loading === "refund" ? <Loader2 className="animate-spin" size={16} /> : <Undo2 size={16} />} Refund deposit ({money(deposit)})
        </button>
        <button onClick={() => run("forfeit")} disabled={!!loading} data-testid="cancel-forfeit-btn" className="w-full justify-center rounded-full border border-[#E5E0D8] py-2.5 text-sm text-[#4A5D6B] hover:border-[#B5794E] hover:text-[#B5794E] disabled:opacity-60 flex items-center gap-2">
          {loading === "forfeit" ? <Loader2 className="animate-spin" size={16} /> : <Ban size={16} />} Forfeit deposit (keep {money(deposit)})
        </button>
        <p className="text-[11px] text-[#8FA89B] text-center mt-3">Refunds reverse the online deposit charge. Forfeiting cancels the visit but keeps the deposit.</p>
      </div>
    </div>
  );
}

function CardInner({ row, onClose, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [saveCard, setSaveCard] = useState(true);
  const amount = Number(row.balance_due || 0);

  const chargeSaved = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/pos/balances/${row.kind}/${row.id}/resolve`, { method: "saved" });
      if (data.status === "succeeded") return onPaid();
      if (data.client_secret) {
        const res = await stripe.confirmCardPayment(data.client_secret);
        if (res.error) throw new Error(res.error.message);
        const c = await api.post(`/pos/balances/${row.kind}/${row.id}/resolve/confirm`, { payment_intent_id: data.payment_intent_id });
        if (c.data.status === "succeeded") return onPaid();
      }
      throw new Error("Card on file could not be charged.");
    } catch (e) {
      toast.error(e.response?.data?.detail != null ? formatApiError(e.response.data.detail) : (e.message || "Charge failed."));
    } finally { setLoading(false); }
  };

  const chargeNew = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/pos/balances/${row.kind}/${row.id}/resolve`, { method: "card", save_card: saveCard });
      if (data.status === "succeeded") return onPaid();
      const card = elements.getElement(CardElement);
      const res = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: { card, billing_details: { name: row.customer_name || "Member" } },
      });
      if (res.error) throw new Error(res.error.message);
      const c = await api.post(`/pos/balances/${row.kind}/${row.id}/resolve/confirm`, { payment_intent_id: data.payment_intent_id });
      if (c.data.status === "succeeded") return onPaid();
      throw new Error("Payment did not complete.");
    } catch (e) {
      toast.error(e.response?.data?.detail != null ? formatApiError(e.response.data.detail) : (e.message || "Charge failed."));
    } finally { setLoading(false); }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7" data-testid="balance-card-modal">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CreditCard size={22} className="text-[#144F75]" /> Charge balance</h3>
        <button onClick={onClose} data-testid="balance-card-cancel" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">{row.customer_name} · <span className="font-semibold text-[#144F75]">{money(amount)}</span> due</p>

      {row.has_card_on_file && (
        <button onClick={chargeSaved} disabled={loading} data-testid="balance-charge-saved" className="eyw-btn w-full justify-center mb-3 disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Charge card on file
        </button>
      )}

      <p className="text-xs font-semibold text-[#4A5D6B] uppercase tracking-wide mb-2">{row.has_card_on_file ? "Or use a new card" : "Enter card"}</p>
      <div className="rounded-xl border border-[#E5E0D8] px-3.5 py-3.5 bg-white mb-3" data-testid="balance-card-element">
        <CardElement options={CARD_STYLE} />
      </div>
      <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer mb-4">
        <input type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} className="accent-[#144F75]" data-testid="balance-save-card" />
        Save this card on file
      </label>
      <button onClick={chargeNew} disabled={loading || !stripe} data-testid="balance-card-pay" className="eyw-btn w-full justify-center disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />} Charge {money(amount)}
      </button>
      <p className="text-[11px] text-[#8FA89B] text-center mt-3 flex items-center justify-center gap-1"><Lock size={11} /> Card details are entered securely in Stripe.</p>
    </div>
  );
}

function BalanceCardModal({ row, publishableKey, onClose, onPaid }) {
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      {stripePromise ? (
        <Elements stripe={stripePromise}><CardInner row={row} onClose={onClose} onPaid={onPaid} /></Elements>
      ) : (
        <div className="bg-white rounded-2xl p-6 max-w-sm text-center">
          <p className="text-sm text-[#4A5D6B]">Stripe is not configured for card payments.</p>
          <button onClick={onClose} className="eyw-btn mt-4">Close</button>
        </div>
      )}
    </div>
  );
}
